import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { CashFlow } from "./dashboard";

/** One category's part of a month. */
export interface CategoryShare {
  category_id: string;
  name: string;
  /** A positive magnitude, rolled up over the category and its descendants. */
  amount_minor: number;
  /**
   * The part of the period's total in basis points: 2929 is 29.29%.
   *
   * Apportioned by the server rather than divided here, so that the ring closes
   * exactly: rounding each share on its own loses basis points between them, and
   * a segment short of a whole turn is a gap the eye finds immediately.
   */
  share_bp: number;
  lines: number;
}

/** A month's money of one kind, split by category. */
export interface Breakdown {
  total_minor: number;
  /** Top-level categories only, biggest first. */
  categories: CategoryShare[];
  uncategorised_minor: number;
  uncategorised_lines: number;
  uncategorised_share_bp: number;
}

/** One payment, as the report lists it. */
export interface Outgoing {
  transaction_id: string;
  booked_date: string;
  /** The resolved payee, falling back to what was typed; empty when neither. */
  payee: string;
  /**
   * Empty when the booking has no category — and also when its splits disagree,
   * because a receipt divided between two categories has no single one and
   * naming either would be a small lie in a column people scan quickly.
   */
  category_name: string;
  /** Negative, as `model.md` §2.2 rule 1 holds money going out. */
  amount_minor: number;
  splits: number;
}

/**
 * One whole month, and the one before it.
 *
 * The difference from `Dashboard` is the word *whole*. A dashboard counts the
 * running month only as far as it has happened, because "am I okay this month?"
 * against a finished month would alarm somebody about the calendar. A report is
 * usually read about a month that has ended.
 */
export interface MonthlyReport {
  period_start: string;
  period_end: string;
  currency_code: string;
  cashflow: CashFlow;
  /** Whole against whole once the month has ended; same stretch while running. */
  previous: CashFlow;
  delta_minor: number;
  /** -1, 0 or +1, decided by the server so two screens cannot disagree. */
  direction: number;
  /** False when the previous month has nothing in it, and no arrow is drawn. */
  is_meaningful: boolean;
  spending: Breakdown;
  income: Breakdown;
  /** The month's biggest payments, biggest first. */
  outgoings: Outgoing[];
}

export const reportQueryKey = (householdId: string, period: string) =>
  ["households", householdId, "reports", "monthly", period] as const;

/**
 * A month's report.
 *
 * `period` is any day inside the month, which is the contract the budget screen
 * already speaks — one convention for "which month", not two.
 */
export function useMonthlyReport(householdId: string | undefined, period: string) {
  return useQuery({
    queryKey: reportQueryKey(householdId ?? "", period),
    queryFn: ({ signal }) =>
      apiFetch<MonthlyReport>(
        `/api/v1/households/${householdId}/reports/monthly?period=${period}`,
        { signal },
      ),
    enabled: Boolean(householdId),
  });
}

/** A share in basis points as a percentage string's worth of a number. */
export const sharePercent = (bp: number) => bp / 100;
